import { Component, Project } from "projen/lib";

/**
 * Return the project and/or subprojects that have a component of a given type.
 * Walks one level of subprojects (does not recurse into sub-subprojects).
 */
export function findProjectsWithComponent<T extends Component>(
  project: Project,
  ctor: new (...args: any[]) => T,
): Array<Project> {
  const out: Array<Project> = [];
  if (project.components.some((c) => c instanceof ctor)) {
    out.push(project);
  }
  for (const sub of project.subprojects) {
    if (sub.components.some((c) => c instanceof ctor)) {
      out.push(sub);
    }
  }
  return out;
}

/**
 * Return the project and/or subprojects that declare a dependency with the
 * given name. Walks one level of subprojects.
 */
export function findProjectsWithDep(
  project: Project,
  name: string,
): Array<Project> {
  const out: Array<Project> = [];
  if (project.deps.all.some((d) => d.name === name)) {
    out.push(project);
  }
  for (const sub of project.subprojects) {
    if (sub.deps.all.some((d) => d.name === name)) {
      out.push(sub);
    }
  }
  return out;
}

/**
 * Return the project and/or subprojects that contain a file with the given
 * name. Walks one level of subprojects.
 */
export function findProjectsWithFile(
  project: Project,
  filename: string,
): Array<Project> {
  const out: Array<Project> = [];
  if (project.tryFindFile(filename) !== undefined) {
    out.push(project);
  }
  for (const sub of project.subprojects) {
    if (sub.tryFindFile(filename) !== undefined) {
      out.push(sub);
    }
  }
  return out;
}

/**
 * Check whether a component of a given type exists on the project or any subproject.
 */
export function hasComponent<T extends Component>(
  project: Project,
  ctor: new (...args: any[]) => T,
): boolean {
  return findProjectsWithComponent(project, ctor).length > 0;
}

/**
 * Check whether a dependency exists on the project or any subproject.
 */
export function hasDep(project: Project, name: string): boolean {
  return findProjectsWithDep(project, name).length > 0;
}

/**
 * Check whether a file exists on the project or any subproject.
 */
export function hasFile(project: Project, filename: string): boolean {
  return findProjectsWithFile(project, filename).length > 0;
}
